import { useEffect, useState } from "react";

type ListSummariesResponse = { results?: { id?: number }[]; total: number };
type ListSummariesFn<TQuery> = (
  params: TQuery & { page: number; limit: number },
) => Promise<ListSummariesResponse>;

/**
 * Resolves a bulk-action's target ids: the explicitly-selected rows as-is, or — when
 * "select all" is active — every matching id across all pages, paginated through `listSummaries`.
 * Shared by the Sites and Site-Groups "Upload Catalog" bulk actions.
 */
export function useResolveAllSelectedIds<
  TQuery extends Record<string, unknown>,
>({
  isAllSelected,
  selectedIds,
  finalQuery,
  listSummaries,
}: {
  isAllSelected: boolean;
  selectedIds: number[];
  finalQuery: TQuery;
  listSummaries: ListSummariesFn<TQuery>;
}): { ids: number[] | null; isLoading: boolean; error: string | null } {
  const [resolvedAllIds, setResolvedAllIds] = useState<number[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAllSelected) {
      setResolvedAllIds(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setResolvedAllIds(null);
    setError(null);

    (async () => {
      try {
        const pageSize = 100;
        let page = 1;
        let totalPages = 1;
        const collectedIds: number[] = [];

        while (page <= totalPages) {
          const response = await listSummaries({
            ...finalQuery,
            page,
            limit: pageSize,
          });

          const ids = (response.results ?? [])
            .map((row) => row.id)
            .filter((id): id is number => id !== undefined);

          collectedIds.push(...ids);
          totalPages = Math.max(1, Math.ceil(response.total / pageSize));
          page += 1;
        }

        if (!cancelled) setResolvedAllIds(collectedIds);
      } catch (err: any) {
        if (!cancelled)
          setError(
            err?.body?.message || "Failed to resolve the full selection",
          );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAllSelected, finalQuery]);

  return {
    ids: isAllSelected ? resolvedAllIds : selectedIds,
    isLoading: isAllSelected && resolvedAllIds === null && !error,
    error,
  };
}
